import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Seo, MaskedLines, Reveal, ParallaxImage, scrollToId } from "../components/Seo";
import { Eyebrow, SectionHead, BtnPrimary, BtnGhost, Marquee, StatusPill, CtaBand, GroupSection, ShotPlaceholder } from "../components/blocks";
import { IMG, LOGOS, PRODUCTS, SERVICES, INDUSTRIES } from "../lib/site";

const MARQUEE_ITEMS = [
  "SoftEbox",
  "myID",
  "CoFundApp",
  "Solution Architecture",
  "Cloud & Infrastructure",
  "Cybersecurity",
  "Software Engineering",
  "Systems Integration",
  "Data & Digital Transformation",
];

const TRUST_ITEMS = [
  {
    title: "Secure system design",
    desc: "Security is designed into system architecture from the first diagram — not bolted on before launch.",
    icon: "M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z",
  },
  {
    title: "Privacy and data protection",
    desc: "Data protection aligned to the Nigeria Data Protection Act 2023 and the processing we actually perform.",
    icon: "M6 10V8a6 6 0 1112 0v2m-14 0h16v10H4V10z",
  },
  {
    title: "Enterprise architecture",
    desc: "Deliberate target architectures and decision records, so systems fit together instead of accumulating.",
    icon: "M4 5h16M4 12h16M4 19h16",
  },
  {
    title: "Quality assurance",
    desc: "Automated testing, code review and staged delivery keep quality a process, not a hope.",
    icon: "M20 6L9 17l-5-5",
  },
  {
    title: "Integration governance",
    desc: "Documented interfaces, versioning and monitoring govern every integration we build or join.",
    icon: "M6 3v12a4 4 0 004 4h8m0 0l-4-4m4 4l-4 4M18 3a3 3 0 100 6 3 3 0 000-6zM6 3a3 3 0 100 6 3 3 0 000-6z",
  },
  {
    title: "Operational reliability",
    desc: "Monitoring, runbooks and incident procedures come standard with the systems we deliver.",
    icon: "M22 12h-4l-3 9L9 3l-3 9H2",
  },
];

function ProductCard({ p, big = false, delay = 0 }) {
  const cardScreenshot = p.screenshots?.recipient?.image;

  return (
    <Reveal delay={delay} className={big ? "lg:col-span-8" : "lg:col-span-4"}>
      <article
        data-testid={`home-product-card-${p.slug}`}
        className="group h-full flex flex-col border border-charcoal/10 bg-white hover:border-charcoal/30 hover:-translate-y-1 transition-[border-color,transform] duration-200"
      >
        <div className="flex items-center gap-4 p-7 pb-5" style={{ background: p.logoBg }}>
          {cardScreenshot ? (
            <img
              src={cardScreenshot}
              alt={`${p.name} interface`}
              className="h-14 w-24 object-cover object-top rounded-[4px] border border-white/25 shadow-md shrink-0 group-hover:scale-105 transition-transform duration-300"
            />
          ) : p.logo ? (
            <img src={p.logo} alt={`${p.name} logo`} className="h-14 w-auto rounded-[3px]" />
          ) : (
            <span className="h-14 w-24 flex items-center justify-center bg-[#0a2e24] border border-white/15 rounded-[3px] font-display font-bold text-white text-lg">
              {p.logoMonogram}
            </span>
          )}
          <div>
            <h3 className={`font-display font-bold text-xl ${p.darkBand ? "text-white" : "text-charcoal"}`}>{p.name}</h3>
            <p className={`text-xs font-mono uppercase tracking-[0.15em] mt-0.5 ${p.darkBand ? "text-white/60" : "text-charcoal/60"}`}>{p.tagline}</p>
          </div>
        </div>
        <div className="flex flex-col flex-1 p-7 pt-6">
          <p className="text-[15px] leading-relaxed text-charcoal/75">{p.short}</p>
          <p className="mt-4 text-sm leading-relaxed text-charcoal/55 border-l-2 border-lime pl-4">
            <span className="font-semibold text-charcoal/70">Built for:</span> {p.useCase}
          </p>
          <div className="mt-auto pt-6 flex flex-wrap items-center justify-between gap-4">
            <StatusPill status={p.status} dark={false} />
            <Link
              to={`/products/${p.slug}`}
              data-testid={`home-product-link-${p.slug}`}
              className="inline-flex items-center gap-1.5 font-semibold text-sm text-charcoal hover:text-charcoal/70 transition-colors duration-200"
            >
              Explore Product <ArrowUpRight size={16} className="text-lime" />
            </Link>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Softline Nigeria — Digital Products & Enterprise Technology Solutions"
        description="Digital products and enterprise technology services for Nigerian enterprises, government and financial institutions — from the Softline group."
        path="/"
      />

      {/* D1+D2 — Hero */}
      <section className="relative bg-charcoal noise-bg bg-grid-dark overflow-hidden" data-testid="home-hero">
        <div className="shell pt-32 md:pt-44 pb-16 md:pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal y={12}>
                <Eyebrow dark>Digital Products &amp; Enterprise Solutions</Eyebrow>
              </Reveal>
              <MaskedLines
                as="h1"
                lines={["Engineering Digital", "Solutions. Enabling", "Business Transformation."]}
                delay={0.15}
                className="font-display font-bold tracking-tight text-white text-[2.6rem] sm:text-6xl lg:text-[4.35rem] leading-[1.02] mt-7"
              />
              <Reveal delay={0.55}>
                <p className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-softgrey">
                  We build innovative digital products and deliver enterprise technology solutions that help organizations
                  modernize, secure and scale.
                </p>
              </Reveal>
              <Reveal delay={0.65}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <BtnPrimary to="/products" testid="hero-cta-products">
                    Explore Our Solutions
                  </BtnPrimary>
                  <BtnGhost to="/contact" testid="hero-cta-experts">
                    Talk to Our Experts
                  </BtnGhost>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5 relative">
              <Reveal delay={0.4}>
                <div className="relative border border-white/15 bg-ink/60 p-2">
                  <span className="absolute -top-px -left-px h-6 w-6 border-t-2 border-l-2 border-lime" aria-hidden="true"></span>
                  <span className="absolute -bottom-px -right-px h-6 w-6 border-b-2 border-r-2 border-lime" aria-hidden="true"></span>
                  <ParallaxImage
                    src={IMG.hero}
                    alt="Enterprise data infrastructure — illustrative photography"
                    className="aspect-[4/3] w-full"
                    distance={36}
                  />
                  <div className="flex items-center justify-between px-4 py-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-softgrey/80">
                      Enterprise-grade delivery
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lime">SL / 01</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.8}>
            <button
              onClick={() => scrollToId("home-intro")}
              data-testid="hero-scroll-hint"
              aria-label="Scroll to introduction"
              className="mt-14 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-softgrey/70 hover:text-lime transition-colors duration-200"
            >
              Scroll <ArrowDown size={14} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* D3 — Corporate introduction */}
      <section id="home-intro" className="bg-surface" data-testid="home-intro">
        <div className="shell py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <SectionHead
                eyebrow="Who we are"
                title="Technology Expertise. Practical Innovation."
                lead="Softline Technologies and Services Nigeria Limited builds proprietary digital products and delivers enterprise technology services for Nigerian organisations. We help enterprises, government institutions and financial organisations modernize core systems, secure digital operations and launch new digital services — combining product engineering, solution architecture and disciplined delivery. As the Nigerian subsidiary of Sweden's Softline Technologies and Services AB, we bring international engineering standards to Nigeria's digital economy, with local focus and accountability."
              />
              <Reveal delay={0.2}>
                <BtnGhost to="/about" dark={false} testid="intro-cta-about" className="mt-9">
                  Learn About Us
                </BtnGhost>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <figure className="relative border border-charcoal/10 bg-white p-2">
                  <ParallaxImage
                    src={IMG.lagos}
                    alt="Lagos, Nigeria — illustrative photography of the Nigerian market"
                    className="aspect-[4/5] w-full"
                    distance={32}
                  />
                  <figcaption className="flex items-center justify-between px-4 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/60">Nigerian market focus</span>
                    <span className="h-2 w-2 bg-lime" aria-hidden="true"></span>
                  </figcaption>
                </figure>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial marquee */}
      <Marquee items={MARQUEE_ITEMS} />

      {/* D4 — Products */}
      <section className="bg-white" data-testid="home-products">
        <div className="shell py-24 md:py-32">
          <SectionHead
            eyebrow="Digital products"
            title="Digital Products Built for Real-World Challenges."
            lead="Proprietary platforms owned and engineered by Softline — each one designed around a specific operational problem, each labelled with its true readiness status."
          />
          <div className="mt-14 grid lg:grid-cols-12 gap-6">
            {PRODUCTS.map((p, i) => (
              <ProductCard key={p.slug} p={p} big={i === 0} delay={i * 0.08} />
            ))}
            <Reveal delay={0.24} className="lg:col-span-8">
              <Link
                to="/products"
                data-testid="home-products-view-all"
                className="group flex h-full min-h-[180px] items-center justify-between gap-6 bg-charcoal p-8 md:p-10 hover:bg-ink transition-colors duration-200"
              >
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">Product portfolio</p>
                  <p className="font-display font-bold text-2xl md:text-3xl text-white mt-3">
                    See every product, its status and engagement options.
                  </p>
                </div>
                <span className="h-12 w-12 shrink-0 flex items-center justify-center bg-lime text-charcoal">
                  <ArrowUpRight size={22} />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* D5 — Services */}
      <section className="bg-surface" data-testid="home-services">
        <div className="shell py-24 md:py-32">
          <SectionHead
            eyebrow="Enterprise services"
            title="Technology Expertise That Moves Business Forward."
            lead="Six practice areas covering the full enterprise technology lifecycle — from architecture and cloud to security, engineering, integration and data."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05} className="h-full">
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-card-${s.slug}`}
                  className="group flex flex-col h-full bg-surface p-8 hover:bg-white transition-colors duration-200"
                >
                  <span className="font-mono text-xs text-charcoal/50">{s.num}</span>
                  <h3 className="font-display font-bold text-xl mt-4 flex items-center justify-between gap-3">
                    {s.name}
                    <ArrowUpRight size={18} className="text-charcoal/30 group-hover:text-lime group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[color,transform] duration-200" />
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{s.short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* D6 — Industries (dark) */}
      <section className="bg-charcoal noise-bg" data-testid="home-industries">
        <div className="shell py-24 md:py-32">
          <SectionHead
            dark
            eyebrow="Industries"
            title="Solutions Designed Around Real Operational Needs."
            lead="Sectors where our products and services are built to create value — described as capabilities and use cases, not as customer claims."
          />
          <div className="mt-14 border-t border-white/10">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 0.04}>
                <Link
                  to="/industries"
                  data-testid={`home-industry-row-${i}`}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 items-center border-b border-white/10 py-7 px-2 md:px-4 hover:bg-white/5 transition-colors duration-200"
                >
                  <span className="md:col-span-1 font-mono text-xs text-lime">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="md:col-span-4 font-display font-bold text-xl md:text-2xl text-white">{ind.name}</h3>
                  <p className="md:col-span-6 text-sm md:text-[15px] leading-relaxed text-softgrey">{ind.desc}</p>
                  <span className="hidden md:flex md:col-span-1 justify-end">
                    <ArrowUpRight size={22} className="text-white/30 group-hover:text-lime transition-colors duration-200" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* D7 — International group */}
      <GroupSection />

      {/* D8 — Security, delivery and trust (dark) */}
      <section className="bg-ink noise-bg" data-testid="home-trust">
        <div className="shell py-24 md:py-32">
          <SectionHead
            dark
            eyebrow="Security, delivery & trust"
            title="Built on Sound Engineering Principles."
            lead="The practices we hold every engagement to — stated as working methods, not as certifications we have not earned."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {TRUST_ITEMS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05} className="h-full">
                <div className="h-full bg-ink p-8" data-testid={`home-trust-item-${i}`}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#D5FF2D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d={t.icon} />
                  </svg>
                  <h3 className="font-display font-bold text-lg text-white mt-5">{t.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-softgrey">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <BtnGhost to="/technology-security" testid="home-trust-link" className="mt-10">
              How we engineer <ArrowUpRight size={16} />
            </BtnGhost>
          </Reveal>
        </div>
      </section>

      {/* D9 — Final CTA */}
      <CtaBand />
    </>
  );
}
